import React from "react";
import i18n, { useTranslation } from "utilities/i18n";
import * as yup from "yup";
import { generatePath } from "react-router";
import { Formik } from "formik";
import {
  Input,
  StandardFormSelect,
  AddressInput,
  Form,
  useNotification,
  DISMISS,
  FormikHelpers
} from "collections";
import { Link, Redirect } from "polyrouter";
import {
  useCreateRegisteredEdgeDeviceMutation,
  useEditDeviceDetailsMutation,
  useUpdateSiteMutation,
  useAvailableRelaySoftwareOvaQuery,
  useAvailableRelaySoftwareVhdQuery
} from "generated/graphql";
import useSitesAndRooms from "routes/Manage/Register/useSitesAndRooms";
import {
  PolyIcon,
  LoadingIndicator,
  SubmitButton,
  useFeatureFlagWithTenant
} from "elements";
import { SyncSiteAddress } from "routes/Manage/Register/RegisterDevice";
import { Layout } from "views/Main/Layouts";
import "./Relay.scss";

const RegisterFormSchema = yup.object({
  name: yup
    .string()
    .ensure()
    .label(i18n.t("RELAY_NAME")),
  site: yup
    .string()
    .ensure()
    .label(i18n.t("RELAY_SITE")),
  room: yup
    .string()
    .ensure()
    .label(i18n.t("RELAY_ROOM")),
  siteAddress: yup.object({
    address1: yup
      .string()
      .ensure()
      .label(i18n.t("RELAY_ADDRESS")),
    geohash: yup
      .string()
      .ensure()
      .label(i18n.t("GEOHASH"))
  }),
  extras: yup.object({
    fromLookup: yup.boolean().default(false)
  }),
  deviceId: yup
    .string()
    .required()
    .ensure()
    .label(i18n.t("RELAY_DEVICE_ID")),
  accessKey: yup
    .string()
    .required()
    .ensure()
    .label(i18n.t("RELAY_ACCESS_KEY"))
});

type RegisterForm = yup.InferType<typeof RegisterFormSchema>;

export const RelaySettings: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { t } = useTranslation();
  const { data: dataOva } = useAvailableRelaySoftwareOvaQuery();
  const { data: dataVhd } = useAvailableRelaySoftwareVhdQuery();
  const [isEnabled, , clientReady] = useFeatureFlagWithTenant("EDGE_RELAY");

  if (!clientReady) {
    return <LoadingIndicator />;
  }

  if (!isEnabled) {
    return <Redirect to="/home" />;
  }

  const downloadUrlOva =
    dataOva?.availableProductSoftwareByPid?.productBuild?.archiveUrl ??
    undefined;

  const downloadUrlVhd =
    dataVhd?.availableProductSoftwareByPid?.productBuild?.archiveUrl ??
    undefined;

  return (
    <Layout.Content className="relay-content" scrolling>
      <Layout.Content.ScrollingList className="scrollable-relay-content">
        <div className="card title-card">{t("RELAY")}</div>

        <div className="ph-2 mb-1">
          <label>{t("RELAY_DOWNLOAD_SUBTITLE")}</label>
        </div>
        <div className="ph-3 mb-2">
          <p className="mb-1">{t("RELAY_DOWNLOAD_INFO")}</p>
          <a className="btn btn-primary" href={downloadUrlOva}>
            {t("RELAY_DOWNLOAD_SUBMIT_OVA")}
          </a>
          <a className="btn btn-primary" href={downloadUrlVhd}>
            {t("RELAY_DOWNLOAD_SUBMIT_VHD")}
          </a>
        </div>

        <div className="ph-2 mb-1">
          <label>{t("RELAY_REGISTER_SUBTITLE")}</label>
        </div>
        <div className="ph-3 register-edge-relay">
          <p className="mb-15">{t("RELAY_REGISTER_INFO")}</p>
          <RelayRegisterForm tenantId={tenantId} />
        </div>
      </Layout.Content.ScrollingList>
    </Layout.Content>
  );
};

const RelayRegisterForm: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { t } = useTranslation();
  const { sitesWithNames, siteMeta } = useSitesAndRooms();
  const [addNotification] = useNotification();
  const [, createRegisteredDevice] = useCreateRegisteredEdgeDeviceMutation();
  const [, updateSite] = useUpdateSiteMutation();
  const [, editDeviceDetails] = useEditDeviceDetailsMutation();

  const requestKey = async (deviceId: string) => {
    const { data, error } = await createRegisteredDevice({
      device: {
        tenantId,
        deviceId
      }
    });

    const response = data?.createRegisteredEdgeDevice;
    if (
      error ||
      response?.error ||
      !response?.success ||
      !response.isEdgeDevice
    ) {
      addNotification({
        type: "error",
        message: t("RELAY_GET_KEY_ERROR"),
        dismiss: DISMISS.USER_ONLY
      });
      return null;
    }

    return response.deviceKey;
  };

  const onSubmit = async (
    values: RegisterForm,
    helpers: FormikHelpers<RegisterForm>
  ) => {
    if (values.site) {
      const updateSiteResponse = await updateSite(
        {
          site: {
            id: siteMeta?.sites.find(site => site.name === values.site)?.id,
            name: values.site,
            tenantId,
            address: values.siteAddress
          }
        },
        {
          notify: { i18nKey: "ERROR_UPDATING_SITE", dismiss: DISMISS.USER_ONLY }
        }
      );
      if (updateSiteResponse.error) return;
    }

    const editDeviceResponse = await editDeviceDetails(
      {
        id: values.deviceId,
        fields: { name: values.name, site: values.site, room: values.room }
      },
      {
        notify: { i18nKey: "ERROR_UPDATING_DEVICE", dismiss: DISMISS.USER_ONLY }
      }
    );
    if (editDeviceResponse.error) return;

    addNotification({
      type: "success",
      message: (
        <span>
          {t("RELAY_REGISTRATION_SUCCESS_1")}
          <Link
            to={generatePath("/manage/inventory/details/:deviceId", {
              deviceId: values.deviceId
            })}
          >
            {t("RELAY_REGISTRATION_SUCCESS_2")}
          </Link>
          {t("RELAY_REGISTRATION_SUCCESS_3")}
        </span>
      ),
      dismiss: DISMISS.SLOW
    });

    helpers.setStatus({ completed: true });
  };

  const getRoomsFromSite = (siteName: string) => {
    const rooms = siteMeta?.sites.find(site => site.name === siteName)?.rooms;
    return rooms?.map(({ name }) => ({ text: name, value: name })) ?? [];
  };

  return (
    <Formik
      title={t("RELAY_REGISTER_TITLE")}
      name="RegisterEdgeRelayModal"
      initialValues={RegisterFormSchema.default()}
      validationSchema={RegisterFormSchema}
      onSubmit={onSubmit}
    >
      {({
        values,
        setFieldValue,
        dirty,
        isSubmitting,
        isValid,
        status,
        resetForm
      }) => {
        return (
          <Form>
            <SyncSiteAddress
              initialValues={RegisterFormSchema.default()}
              siteNameKey="site"
            />
            <div className="grid-container mb-2 mh-4">
              <label htmlFor="relayName-input">{t("RELAY_NAME")}</label>
              <Input name="name" tabIndex={1} disabled={status?.completed} />

              <label>{t("RELAY_SITE")}</label>
              <div>
                <StandardFormSelect
                  label={""}
                  name="site"
                  className={status?.completed && "disabled"}
                  disabled={status?.completed}
                  options={sitesWithNames}
                  tabIndex={2}
                  customValue
                  search
                  selectOnInput
                />
              </div>

              <label>{t("RELAY_ROOM")}</label>
              <div>
                <StandardFormSelect
                  label={""}
                  name="room"
                  className={status?.completed && "disabled"}
                  disabled={status?.completed}
                  options={getRoomsFromSite(values.site)}
                  tabIndex={3}
                  customValue
                  search
                  selectOnInput
                />
              </div>

              <label htmlFor="address-input">{t("RELAY_ADDRESS")}</label>
              <AddressInput
                tabIndex={4}
                name="siteAddress.address1"
                onSelect={address => {
                  setFieldValue("siteAddress", {
                    address1: address.formattedAddress,
                    geohash: address.geohash
                  });
                }}
                disabled={status?.completed}
              />

              <label htmlFor="deviceId-input">{t("RELAY_DEVICE_ID")}</label>
              <Input
                name="deviceId"
                data-testid="relay-id"
                tabIndex={5}
                disabled={!!values.accessKey}
              />

              <label htmlFor="accessKey-input">{t("RELAY_ACCESS_KEY")}</label>
              {values.deviceId && !values.accessKey && (
                <button
                  className="btn btn-link ph-0"
                  data-testid="relay-key-link"
                  type="button"
                  onClick={async () => {
                    const accessKey = await requestKey(values.deviceId);
                    if (accessKey) setFieldValue("accessKey", accessKey);
                  }}
                >
                  {t("RELAY_GET_KEY")}
                </button>
              )}
              {values.accessKey && (
                <>
                  <Input
                    name="accessKey"
                    data-testid="relay-key"
                    tabIndex={6}
                    disabled
                  />
                  <button
                    className="btn btn-link"
                    data-testid="relay-key-copy"
                    type="button"
                    title={t("RELAY_COPY")}
                    onClick={async () => {
                      await navigator.clipboard.writeText(values.accessKey);
                      addNotification({
                        type: "success",
                        message: t("RELAY_COPY_SUCCESS"),
                        dismiss: DISMISS.DEFAULT
                      });
                    }}
                  >
                    <PolyIcon icon="edit_copy" />
                  </button>
                </>
              )}
            </div>

            <SubmitButton
              isSubmitting={isSubmitting}
              testId="relay-submit"
              disabled={!isValid || !dirty || status?.completed}
              labelKey="RELAY_REGISTER_SUBMIT"
              submittingLabelKey="REGISTERING"
            />
            <button
              className="btn"
              type="button"
              disabled={isSubmitting || !dirty}
              onClick={() => {
                resetForm();
              }}
            >
              {t("RELAY_REGISTER_RESET")}
            </button>
          </Form>
        );
      }}
    </Formik>
  );
};
